import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Select from 'react-select';
import { postInquiry } from '../../api';
import products from '../../data/products';
import countryOptions from '../../assets/countries.json';
import './ProductPage.css';

// grecaptcha is loaded globally via the script tag in public/index.html
declare const grecaptcha: {
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY ?? '';

interface FormData {
  name: string;
  email: string;
  phone: string;
  inquiry: string;
  company: string;
  country: string;
}

interface FormErrors {
  name?: string;
  email?: string;
  phone?: string;
  inquiry?: string;
  country?: string;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_REGEX = /^\+?[0-9\s\-().]{7,20}$/;

const validate = (data: FormData): FormErrors => {
  const errors: FormErrors = {};
  if (!data.name.trim()) errors.name = 'Name is required.';
  if (!data.email.trim()) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_REGEX.test(data.email)) {
    errors.email = 'Enter a valid email address.';
  }
  if (!data.phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else if (!PHONE_REGEX.test(data.phone)) {
    errors.phone = 'Enter a valid phone number (7–20 digits, may include +, spaces, dashes).';
  }
  if (!data.inquiry.trim()) errors.inquiry = 'Please describe your inquiry.';
  if (!data.country) errors.country = 'Please select your country.';
  return errors;
};

const EMPTY_FORM: FormData = {
  name: '', email: '', phone: '', inquiry: '', company: '', country: '',
};

const ProductPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const product = productId ? products.find((p) => p.id === productId) ?? null : null;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState<FormData>(EMPTY_FORM);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const openModal = () => {
    setIsModalOpen(true);
    setErrors({});
    setSubmitError(null);
    setIsSuccess(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setFormData(EMPTY_FORM);
    setErrors({});
    setSubmitError(null);
    setIsSuccess(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleCountryChange = (selectedOption: { value: string; label: string } | null) => {
    setFormData((prev) => ({ ...prev, country: selectedOption ? selectedOption.label : '' }));
    if (errors.country) setErrors((prev) => ({ ...prev, country: undefined }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationErrors = validate(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);
    try {
      const recaptchaToken = await grecaptcha.execute(RECAPTCHA_SITE_KEY, { action: 'submit_product_inquiry' });
      await postInquiry({
        ...formData,
        product: product?.name ?? '',
        company: formData.company || undefined,
        recaptchaToken,
      });
      setIsSuccess(true);
      setFormData(EMPTY_FORM);
      setErrors({});
    } catch {
      setSubmitError(
        'Failed to submit your inquiry. Please try again or contact us directly at info@ravitaj.com.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productId]);

  if (!product) {
    return (
      <div className="product-not-found">
        <h2>Product not found</h2>
        <Link to="/" className="back-link">Return to Home</Link>
      </div>
    );
  }

  return (
    <div className="product-page">
      {/* ── Showcase ── */}
      <div className="product-showcase">
        <div className="product-image-container">
          <img src={product.image} alt={product.name} loading="lazy" />
        </div>
        <div className="product-details">
          <span className="product-eyebrow">Bulk Export Available</span>
          <h2>{product.name}</h2>
          <p className="product-subtitle">
            {product.englishName} &nbsp;·&nbsp; <span className="hindi">{product.hindiName}</span>
          </p>
          <p className="product-botanical">
            <em>Botanical name:</em> {product.botanicalName}
          </p>

          <div className="product-specs">
            <div className="spec-item">
              <span className="spec-label">Available Forms</span>
              <span className="spec-value">Raw Herb · Powder · Extract</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Packaging</span>
              <span className="spec-value">25 kg · 50 kg bags &amp; custom</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Documentation</span>
              <span className="spec-value">COA · MSDS · Phytosanitary cert.</span>
            </div>
            <div className="spec-item">
              <span className="spec-label">Certifications</span>
              <span className="spec-value">GMP · ISO 9001:2015 · FSSAI</span>
            </div>
          </div>

          <div className="showcase-cta-group">
            <button className="inquire-now-button" onClick={openModal}>
              Request a Quote
            </button>
            <a
              href={`https://wa.me/919624797077?text=${encodeURIComponent(
                `Hi, I'm interested in bulk ${product.name}. Could you share pricing and availability?`
              )}`}
              className="whatsapp-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              {/* WhatsApp icon */}
              <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413z" />
              </svg>
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* ── About ── */}
      <div className="product-overview-container">
        <div className="product-overview">
          <div className="product-description">
            <h3>About {product.name}</h3>
            <p>{product.description}</p>
          </div>
        </div>
      </div>

      {/* ── Ayurvedic Properties ── */}
      <div className="product-details-container">
        <div className="product-sections">
          <div className="product-section">
            <h3>Ayurvedic Properties</h3>
            <div className="properties-grid">
              <div className="property">
                <h4>Rasa (Taste)</h4>
                <p>{product.ayurvedicProperties.rasa}</p>
              </div>
              <div className="property">
                <h4>Virya (Potency)</h4>
                <p>{product.ayurvedicProperties.virya}</p>
              </div>
              <div className="property">
                <h4>Vipaka (Post-digestive effect)</h4>
                <p>{product.ayurvedicProperties.vipaka}</p>
              </div>
              <div className="property">
                <h4>Effect on Doshas</h4>
                <p>{product.ayurvedicProperties.doshaEffect}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Uses & Benefits ── */}
      <div className="uses-benefits-section">
        <h3>Uses & Benefits</h3>
        <ul className="benefits-list">
          {product.usesAndBenefits.map((benefit, index) => (
            <li key={index}>{benefit}</li>
          ))}
        </ul>
      </div>

      {/* ── Back link ── */}
      <div className="back-to-products">
        <Link to="/" className="back-link">← Back to All Products</Link>
      </div>

      {/* ── Inquiry Modal ── */}
      {isModalOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div className="modal">
            {isSuccess ? (
              <div className="modal-success">
                <div className="success-icon">✓</div>
                <h3>Inquiry Sent!</h3>
                <p>
                  Thank you for your interest in <strong>{product.name}</strong>. We will get back
                  to you within 1–2 business days.
                </p>
                <button className="close-button" onClick={closeModal}>Close</button>
              </div>
            ) : (
              <>
                <h3 id="modal-title">Request a Quote — {product.name}</h3>
                <form onSubmit={handleSubmit} noValidate>
                  <div className="modal-field">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your Name *"
                      aria-label="Name"
                    />
                    {errors.name && <span className="field-error">{errors.name}</span>}
                  </div>
                  <div className="modal-field">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Email Address *"
                      aria-label="Email"
                    />
                    {errors.email && <span className="field-error">{errors.email}</span>}
                  </div>
                  <div className="modal-field">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Phone / WhatsApp * (e.g. +1 555 000 0000)"
                      aria-label="Phone"
                    />
                    {errors.phone && <span className="field-error">{errors.phone}</span>}
                  </div>
                  <div className="modal-field">
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Company Name (Optional)"
                      aria-label="Company"
                    />
                  </div>
                  <div className="modal-field">
                    <Select
                      inputId="modal-country"
                      options={countryOptions}
                      onChange={handleCountryChange}
                      value={countryOptions.find((o) => o.label === formData.country) ?? null}
                      placeholder="Select Country *"
                      isSearchable
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                      aria-label="Country"
                    />
                    {errors.country && <span className="field-error">{errors.country}</span>}
                  </div>
                  <div className="modal-field">
                    <textarea
                      name="inquiry"
                      value={formData.inquiry}
                      onChange={handleChange}
                      placeholder="Inquiry details — quantity required, product form (powder/extract/raw herb), intended use… *"
                      rows={4}
                      aria-label="Inquiry Details"
                    />
                    {errors.inquiry && <span className="field-error">{errors.inquiry}</span>}
                  </div>
                  <p className="recaptcha-notice">
                    This site is protected by reCAPTCHA and the Google{' '}
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>{' '}
                    and{' '}
                    <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>{' '}
                    apply.
                  </p>
                  {submitError && <p className="submit-error">{submitError}</p>}
                  <div className="modal-actions">
                    <button type="submit" className="product-submit-button" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending…' : 'Send Inquiry'}
                    </button>
                    <button type="button" className="close-button" onClick={closeModal}>
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductPage;
